import React, {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';

import {Accordion, AccordionDetails, AccordionSummary, Button, Paper, Stack, Typography} from '@mui/material';

import {useLazyGetCurrentInvoicesListQuery} from '../../../../features/coreApi/apiSlice';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {StyledMarginWidthDividerSm} from '../../@extended/Divider';
import EmptyBox from '../../@extended/EmptyBox';
import LoadingBox from '../../@extended/LoadingBox';

import {getDashboardAccount, getDashboardCommunities, getDashboardCommunity} from '../../../../app/store/reducers/dashboard';
import {fontAwesomeIcons} from '../../../utils/icons';
import CONSTANTS from '../../../constants/envConstants';
import {getIsStakeAdmin, getTeamMemberRole} from '../../../../app/store/reducers/session';

const CommunityInvoices = ({data}) => {
    const userRole = useSelector(getTeamMemberRole);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const dashboardCommunities = useSelector(getDashboardCommunities);

    const handleClick = async (path) => {
        try {
            const encodedFilePath = encodeURIComponent(path);
            const response = await axios({
                url: CONSTANTS.BASE_API_URL + 'invoice/download',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    dealsAPIKey: CONSTANTS.BASE_API_KEY
                },
                params: {file_path: encodedFilePath},
                responseType: 'blob'
            });

            // Get the filename from the Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = path.split('/').pop();
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            // Create a blob from the response data
            const blob = new Blob([response.data], {type: response.headers['content-type']});

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element and trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading Excel file:', error);
            alert('Failed to download Excel file. Please try again.');
        }
    };

    const communityInvoices = useMemo(() => {
        const filteredInvoices = [];

        data.forEach((communityInvoice) => {
            if (userRole != 'Administrator' && !isStakeAdmin) {
                for (const community of dashboardCommunities) {
                    if (community.name == communityInvoice.community_name) {
                        filteredInvoices.push(communityInvoice);
                    }
                }
            } else {
                filteredInvoices.push(communityInvoice);
            }
        });

        return filteredInvoices;
    }, [data, dashboardCommunities, isStakeAdmin, userRole]);

    return (
        <Stack
            direction={'column'}
            justifyContent={'center'}
            divider={<StyledMarginWidthDividerSm />}
            sx={{
                borderTop: '1px solid #e3e3e3'
            }}
        >
            {communityInvoices.map((invoice, idx) => {
                return (
                    <Stack
                        key={`invoice-${idx}`}
                        direction={'row'}
                        alignItems="center"
                        justifyContent={'space-between'}
                        p="0 34px 0 34px"
                        minHeight="48px"
                    >
                        <Typography variant="h6" fontWeight={'fontWeightRegular'}>
                            {invoice.community_name}
                        </Typography>
                        <Button
                            variant="text"
                            color="secondary"
                            size="small"
                            onClick={() => handleClick(invoice.path)}
                            sx={{fontWeight: 500}}
                        >
                            Download
                        </Button>
                    </Stack>
                );
            })}
        </Stack>
    );
};

CommunityInvoices.propTypes = {
    data: PropTypes.array
};

const CurrentInvoices = () => {
    const dashboardAccount = useSelector(getDashboardAccount);
    const dashboardCommunity = useSelector(getDashboardCommunity);

    const [fetchCurrentInvoice, {data: currentInvoice = {}, isLoading, isFetching, isSuccess}] = useLazyGetCurrentInvoicesListQuery();

    useEffect(() => {
        if (dashboardAccount && dashboardAccount.sfid) {
            fetchCurrentInvoice({
                sfid: dashboardAccount.sfid,
                community_sfid: dashboardCommunity.sfid !== 'all' ? dashboardCommunity.sfid : null
            });
        }
    }, [dashboardAccount, dashboardCommunity]);

    const listComponent = useMemo(() => {
        if (isLoading || isFetching) {
            return (
                <Paper
                    sx={{
                        width: '100%',
                        height: 'fit-content',
                        borderRadius: '0',
                        borderWidth: '1px 0 0 0',
                        boxShadow: 'none'
                    }}
                >
                    <LoadingBox />
                </Paper>
            );
        } else if (isSuccess && Object.keys(currentInvoice).length > 0) {
            return <CommunityInvoices data={currentInvoice} />;
        } else {
            return <EmptyBox text={'Current invoices are not found'} height="100px" sx={{borderTop: '1px solid #e3e3e3'}} />;
        }
    }, [isLoading, isFetching, isSuccess, currentInvoice]);

    const mainComponent = useMemo(() => {
        return (
            <Accordion
                key={'past-invoices-accordion'}
                sx={{
                    borderRadius: 0,
                    borderWidth: 0,
                    borderTop: '1px solid #e3e3e3'
                }}
            >
                <AccordionSummary expandIcon={<FontAwesomeIcon icon={fontAwesomeIcons.faChevronDown} />}>
                    <Typography variant="h5" fontSize={'14px'} lineHeight={'21px'} fontWeight={'fontWeightMedium'} sx={{flexGrow: 1}}>
                        Current invoices
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{borderWidth: 0}}>{listComponent}</AccordionDetails>
            </Accordion>
        );
    }, [listComponent]);

    return mainComponent;
};

export default CurrentInvoices;
